import React, { useEffect, useRef, useState } from 'react';
import { imageUrl } from '../../api/endpoints';
import { IconBalance, IconScan } from '../../components/icons/AnalyzerIcons';
import { areSameProductReferences, type ProductReference } from '../../utils/productIdentity';

const clean = (v: string): string => v.replace(/\D/g, '').slice(0, 14);

/**
 * A single A/B product slot shared by the comparison and compatibility screens.
 * Empty state offers Favorites | Barcode; the barcode entry is an inline field
 * (camera scan + clear, confirm on Enter / auto once a product resolves). Once a
 * product is selected it renders a summary with a Replace menu. Colour identity
 * (A teal / B coral) comes from the global `compare-slot--{side}` classes.
 */
export function ProductSlot({
  side,
  product,
  inputRef,
  canScan,
  selectedNote = 'Selected for comparison',
  footer,
  onBarcodeChange,
  onPaste,
  onScan,
  onPickFavorite,
  onClear,
}: {
  side: 'a' | 'b';
  product: ProductReference;
  inputRef: React.RefObject<HTMLInputElement>;
  canScan: boolean;
  selectedNote?: string;
  /** Extra content rendered inside the card, below the product summary, only
   *  when a product is selected (e.g. the compatibility "usage" control). */
  footer?: React.ReactNode;
  onBarcodeChange: (value: string) => void;
  onPaste: () => void;
  onScan: () => void;
  onPickFavorite: () => void;
  onClear: () => void;
}) {
  const [editingBarcode, setEditingBarcode] = useState(false);
  const menuRef = useRef<HTMLDetailsElement>(null);
  // The product that was selected when barcode editing started. Used so we only
  // auto-collapse the editor when a *different* product resolves, not when the
  // editor is opened on an already-selected slot (Replace → Barcode).
  const editStartProductRef = useRef<ProductReference | null>(null);
  const sideLabel = side.toUpperCase();
  const hasSelection = [
    product.productId,
    product.publicId,
    product.sku,
    product.clientId,
    clean(product.barcode ?? '').length >= 8,
  ].some(Boolean);

  // Auto-collapse the barcode editor once a *newly resolved* product appears
  // (camera scan or favorite pick while editing). Opening the editor on an
  // already-selected slot must NOT collapse it, and a plain typed barcode (no
  // id/brand/name) keeps it open until Enter.
  useEffect(() => {
    if (!editingBarcode) return;
    const resolved = product.productId ?? product.publicId ?? product.brand ?? product.name;
    if (!resolved) return;
    if (!areSameProductReferences(product, editStartProductRef.current ?? undefined)) {
      setEditingBarcode(false);
    }
  }, [editingBarcode, product]);

  const closeMenu = () => {
    if (menuRef.current) menuRef.current.open = false;
  };
  const startBarcodeEntry = () => {
    closeMenu();
    editStartProductRef.current = product;
    setEditingBarcode(true);
    window.setTimeout(() => { inputRef.current?.focus(); }, 0);
  };
  const runAction = (action: () => void) => {
    closeMenu();
    action();
  };

  const sourceActions = (inMenu: boolean) => (
    <div className={inMenu ? 'compare-slot__menu-actions' : 'compare-slot__source-grid'}>
      <button
        type="button"
        className="btn btn--ghost btn--sm justify-center"
        onClick={() => { runAction(onPickFavorite); }}
        aria-label={`Choose Product ${sideLabel} from favorites`}
      >
        <IconBalance /> Favorites
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm justify-center"
        onClick={startBarcodeEntry}
        aria-label={`Add Product ${sideLabel} barcode`}
      >
        <span aria-hidden>#</span> Barcode
      </button>
    </div>
  );

  return (
    <section className={`compare-slot compare-slot--${side}`} aria-labelledby={`compare-slot-${side}-title`}>
      <div className="compare-slot__header">
        <span className={`compare-side-badge compare-side-badge--${side}`} aria-hidden>{sideLabel}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 id={`compare-slot-${side}-title`} className="t-small" style={{ margin: 0, fontWeight: 700 }}>
            Product {sideLabel}
          </h2>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            {hasSelection ? selectedNote : 'Choose how to add a product'}
          </p>
        </div>
        {hasSelection && (
          <details ref={menuRef} className="compare-slot__menu">
            <summary className="btn btn--ghost btn--sm">Replace</summary>
            <div className="card card--bordered compare-slot__menu-panel">
              {sourceActions(true)}
            </div>
          </details>
        )}
      </div>

      {hasSelection ? (
        <>
          <ProductSummary product={product} side={side} onClear={onClear} />
          {footer}
        </>
      ) : (
        <div className="compare-slot__empty">
          <p className="t-small t-mute" style={{ margin: 0 }}>From favorites, or a barcode (scan or type) — or use photo scan above.</p>
          {sourceActions(false)}
        </div>
      )}

      {editingBarcode && (
        <div className="compare-slot__barcode-entry">
          <label htmlFor={`compare-barcode-${side}`} className="t-small" style={{ fontWeight: 600 }}>
            Product {sideLabel} barcode
          </label>
          <div className="compare-barcode-field">
            <input
              ref={inputRef}
              id={`compare-barcode-${side}`}
              value={product.barcode ?? ''}
              onChange={(event) => { onBarcodeChange(event.target.value); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && clean(product.barcode ?? '').length >= 8) setEditingBarcode(false);
                if (event.key === 'Escape') setEditingBarcode(false);
              }}
              placeholder="Scan or enter barcode"
              inputMode="numeric"
              pattern="[0-9]*"
              className="compare-barcode-field__input"
            />
            <div className="compare-barcode-field__actions">
              <button
                type="button"
                className="compare-barcode-field__btn"
                onClick={onScan}
                disabled={!canScan}
                aria-label={`Scan Product ${sideLabel} barcode with camera`}
              >
                <IconScan />
              </button>
              <button
                type="button"
                className="compare-barcode-field__btn compare-barcode-field__btn--x"
                onClick={() => {
                  if ((product.barcode ?? '') !== '') {
                    onBarcodeChange('');
                    inputRef.current?.focus();
                  } else {
                    setEditingBarcode(false);
                  }
                }}
                aria-label={(product.barcode ?? '') !== '' ? `Clear Product ${sideLabel} barcode` : 'Close barcode entry'}
              >
                <span aria-hidden>✕</span>
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="t-small t-mute">Scan or type · Enter to confirm</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onPaste}>Paste</button>
          </div>
        </div>
      )}
    </section>
  );
}

function ProductSummary({
  product,
  side,
  onClear,
}: {
  product: ProductReference;
  side: 'a' | 'b';
  onClear: () => void;
}) {
  const title = product.name ?? product.brand ?? (product.productId ? `Product #${product.productId}` : 'Barcode product');
  const showBrand = Boolean(product.brand && product.name);
  const src = product.imageUrl ? imageUrl(product.imageUrl) : null;
  const identity = product.barcode
    ? `Barcode ${product.barcode}`
    : product.sku
      ? `SKU ${product.sku}`
      : product.productId
        ? `Saved product #${product.productId}`
        : 'Selected product';
  return (
    <div
      className={`compare-product-summary compare-product-summary--${side}`}
      style={{ padding: 'var(--s-3)', display: 'flex', gap: 'var(--s-3)', alignItems: 'center' }}
    >
      <div
        style={{
          width: 44, height: 44, flexShrink: 0, borderRadius: 'var(--r-sm)',
          overflow: 'hidden', background: 'var(--c-cream-weak)', display: 'grid', placeItems: 'center',
        }}
      >
        {src
          ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <IconBalance />}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          className="t-small"
          style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {title}
        </div>
        {showBrand && (
          <div
            className="t-small t-mute"
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {product.brand}
          </div>
        )}
        <div className="t-small t-mute row-2" style={{ alignItems: 'center' }}>
          <span className="dot" />
          {identity}
        </div>
      </div>
      <button type="button" onClick={onClear} className="btn btn--ghost btn--sm" aria-label={`Remove Product ${side.toUpperCase()}`}>✕</button>
    </div>
  );
}
